import { toast } from "react-toastify";
import { OBJECT_TYPES, GRID_CELL_SIZE_M } from "../../configuratorConstants";
import { distance } from "../../utils/geometry";

export const handleJointAction = (state, action, context) => {
  const {
    currentFloorObjects,
    currentFloorId,
    t,
    getJointEndpoints,
    recalculateStateAfterGeometryChange,
    validateProjectInteractions,
  } = context;

  switch (action.type) {
    case "SET_CLICKED_JOINT_SEGMENT": {
      return {
        handled: true,
        directState: {
          ...state,
          clickedJointSegmentIndex: action.payload,
        },
      };
    }

    case "TOGGLE_WALL_SEGMENT_IN_JOINT": {
      const { jointId, positionOnSegment } = action.payload;
      const nextStateObjects = JSON.parse(JSON.stringify(currentFloorObjects));
      const joint = nextStateObjects.find(
        (o) => o.id === jointId && o.type === OBJECT_TYPES.DOCKING_JOINT,
      );

      if (joint) {
        const numSlots = Math.max(
          1,
          Math.round(joint.width / GRID_CELL_SIZE_M),
        );
        const targetSlotIndex = Math.floor(positionOnSegment * numSlots);

        if (!joint.activeWallSegments) {
          joint.activeWallSegments = [];
        }

        const segmentExists = joint.activeWallSegments.includes(targetSlotIndex);

        if (segmentExists) {
          joint.activeWallSegments = joint.activeWallSegments.filter(
            (index) => index !== targetSlotIndex,
          );
          setTimeout(() => toast.success(t("toasts.wallDeleted")), 0);
          if (joint.activeWallSegments.length === 0) {
            joint.displayMode = "passage";
          }
        } else {
          joint.activeWallSegments.push(targetSlotIndex);
          joint.displayMode = "custom";
          setTimeout(() => toast.success(t("toasts.wallAdded")), 0);
        }

        return {
          handled: true,
          nextStateObjects,
          needsValidation: true,
        };
      }

      return { handled: true, directState: state };
    }

    case "TOGGLE_JOINT_PASSAGE_SEGMENT": {
      const { jointId, segmentIndex } = action.payload;
      const tempObjects = JSON.parse(JSON.stringify(currentFloorObjects));

      const joint = tempObjects.find(
        (obj) => obj.id === jointId && obj.type === OBJECT_TYPES.DOCKING_JOINT,
      );
      if (!joint) {
        return { handled: true, directState: state };
      }

      if (!joint.passageSegments) {
        joint.passageSegments = [];
      }

      const index = joint.passageSegments.indexOf(segmentIndex);
      if (index > -1) {
        joint.passageSegments.splice(index, 1);
      } else {
        joint.passageSegments.push(segmentIndex);
      }

      return {
        handled: true,
        nextStateObjects: tempObjects,
      };
    }

    case "SET_DOCKING_JOINT_DISPLAY_MODE": {
      const { jointId, mode } = action.payload;
      let tempObjects = JSON.parse(JSON.stringify(currentFloorObjects));

      const allJoints = tempObjects.filter(
        (o) => o.type === OBJECT_TYPES.DOCKING_JOINT,
      );
      const clickedJoint = allJoints.find((j) => j.id === jointId);

      if (!clickedJoint) {
        return { handled: true, directState: state };
      }

      const jointsToUpdate = new Set([clickedJoint]);
      const queue = [clickedJoint];
      const visited = new Set([clickedJoint.id]);
      const tolerance = 0.01;

      while (queue.length > 0) {
        const currentJoint = queue.shift();
        const { p1: currentP1, p2: currentP2 } = getJointEndpoints(currentJoint);

        for (const otherJoint of allJoints) {
          if (visited.has(otherJoint.id)) continue;

          if (
            Math.abs(
              (otherJoint.rotation || 0) - (currentJoint.rotation || 0),
            ) < 1
          ) {
            const { p1: otherP1, p2: otherP2 } = getJointEndpoints(otherJoint);

            if (
              distance(currentP1, otherP2) < tolerance ||
              distance(currentP2, otherP1) < tolerance
            ) {
              jointsToUpdate.add(otherJoint);
              visited.add(otherJoint.id);
              queue.push(otherJoint);
            }
          }
        }
      }

      let newSelectedObjectId = state.selectedObjectId;

      jointsToUpdate.forEach((jointToUpdate) => {
        const joint = tempObjects.find((j) => j.id === jointToUpdate.id);
        if (joint) {
          const previousMode = joint.displayMode;
          joint.displayMode = mode;

          if (mode === "passage") {
            const wasSelectedElementOnThisJoint = joint.elements.some(
              (el) => el.id === state.selectedObjectId,
            );
            joint.elements = [];
            if (wasSelectedElementOnThisJoint) {
              newSelectedObjectId = null;
            }
            joint.passageSegments = [];
          } else if (mode === "double" && previousMode === "single") {
            const wasSelectedElementOnThisJoint = joint.elements.some(
              (el) => el.id === state.selectedObjectId,
            );
            joint.elements = joint.elements.filter(
              (el) =>
                el.type !== OBJECT_TYPES.WINDOW &&
                el.type !== OBJECT_TYPES.PANORAMIC_WINDOW,
            );
            if (wasSelectedElementOnThisJoint) {
              newSelectedObjectId = null;
            }
          }

          if (mode === "single" || mode === "double") {
            joint.activeWallSegments = [];
          }

          if (previousMode === "custom" && mode !== "custom") {
            joint.activeWallSegments = [];
          }
        }
      });

      const recalculatedObjects = recalculateStateAfterGeometryChange(
        tempObjects,
        tempObjects,
        t,
      );
      const nextStateObjects = validateProjectInteractions(
        recalculatedObjects,
        currentFloorId,
        { ...state.projectObjects, [currentFloorId]: recalculatedObjects },
        state.floors,
        t,
      );

      return {
        handled: true,
        nextStateObjects,
        newSelectedObjectId,
        needsRecalculation: false,
        needsValidation: false,
      };
    }

    default:
      return null;
  }
};
